import React from 'react';
import PropTypes from 'prop-types';
import { I18n } from 'react-redux-i18n';

// Aperçu de la disponibilité d'un produit sur la semaine.
// Modèle : chaque créneau coché est une transition (jour + heure). Entre deux
// transitions l'état est tenu. Le calcul est CYCLIQUE : l'état boucle par-dessus
// la fin de semaine (dimanche → lundi). Sans aucune transition → toujours dispo.

const WEEK_MIN = 7 * 24 * 60;
const DAY_MIN = 24 * 60;

// index 0 = lundi ... 6 = dimanche
const DAY_KEYS = [
	'general.monday',
	'general.tuesday',
	'general.wednesday',
	'general.thursday',
	'general.friday',
	'general.saturday',
	'general.sunday',
];

// repères horaires (heures) tracés sur chaque barre
const HOUR_MARKS = [6, 12, 18];

// minute absolue dans la semaine (0 = lundi 00:00)
const toWeekMinute = ts =>
	((ts.day - 1) * 24 + (ts.hour || 0)) * 60 + (ts.minute || 0);

const fmt = min => {
	const h = String(Math.floor(min / 60)).padStart(2, '0');
	const m = String(min % 60).padStart(2, '0');
	return `${h}:${m}`;
};

// Construit les segments colorés couvrant [0, WEEK_MIN) à partir des créneaux cochés.
function buildSegments(timeslots, selection) {
	const transitions = timeslots
		.filter(ts =>
			Object.prototype.hasOwnProperty.call(selection, ts.id)
		)
		.map(ts => ({ m: toWeekMinute(ts), state: selection[ts.id] }))
		.sort((a, b) => a.m - b.m);

	// aucune règle → produit toujours disponible (défaut)
	if (!transitions.length) {
		return [{ start: 0, end: WEEK_MIN, state: 'enable' }];
	}

	const n = transitions.length;
	const segs = [];
	// cyclique : avant la 1re transition, l'état est celui de la dernière (boucle)
	if (transitions[0].m > 0) {
		segs.push({
			start: 0,
			end: transitions[0].m,
			state: transitions[n - 1].state,
		});
	}
	for (let i = 0; i < n; i++) {
		const start = transitions[i].m;
		const end = i < n - 1 ? transitions[i + 1].m : WEEK_MIN;
		if (end > start) segs.push({ start, end, state: transitions[i].state });
	}
	return segs;
}

// Découpe les segments par jour en blocs positionnés (top/height en % de la journée).
function blocksByDay(segments) {
	const days = Array.from({ length: 7 }, () => []);
	segments.forEach(seg => {
		let s = seg.start;
		while (s < seg.end) {
			const day = Math.floor(s / DAY_MIN);
			const dayStart = day * DAY_MIN;
			const e = Math.min(seg.end, dayStart + DAY_MIN);
			days[day].push({
				top: ((s - dayStart) / DAY_MIN) * 100,
				height: ((e - s) / DAY_MIN) * 100,
				state: seg.state,
				startMin: s - dayStart,
				endMin: e - dayStart,
			});
			s = e;
		}
	});
	return days;
}

const WeeklyAvailability = ({ timeslots, selection }) => {
	const segments = buildSegments(timeslots || [], selection || {});
	const days = blocksByDay(segments);

	return (
		<div className="week-availability">
			<div className="week-availability-head">
				<span className="week-availability-title">
					{I18n.t('settings.binding.week_preview_title')}
				</span>
				<span className="week-availability-legend">
					<span className="week-availability-dot is-available" />
					{I18n.t('settings.binding.week_available')}
					<span className="week-availability-dot is-unavailable" />
					{I18n.t('settings.binding.week_unavailable')}
				</span>
			</div>

			<div className="week-availability-grid">
				<div className="week-availability-axis">
					{HOUR_MARKS.map(h => (
						<span
							key={h}
							className="week-availability-axis-mark"
							style={{ top: `${(h / 24) * 100}%` }}>
							{String(h).padStart(2, '0')}h
						</span>
					))}
				</div>

				{days.map((blocks, d) => (
					<div key={d} className="week-availability-day">
						<div className="week-availability-bar">
							{HOUR_MARKS.map(h => (
								<span
									key={h}
									className="week-availability-gridline"
									style={{ top: `${(h / 24) * 100}%` }}
								/>
							))}
							{blocks.map((b, i) => {
								const label =
									b.state === 'disable'
										? I18n.t('settings.binding.week_unavailable')
										: I18n.t('settings.binding.week_available');
								return (
									<div
										key={i}
										className={
											'week-availability-block ' +
											(b.state === 'disable'
												? 'is-unavailable'
												: 'is-available')
										}
										style={{ top: `${b.top}%`, height: `${b.height}%` }}
										title={`${label} · ${fmt(b.startMin)}–${fmt(b.endMin)}`}
									/>
								);
							})}
						</div>
						<span className="week-availability-day-label">
							{I18n.t(DAY_KEYS[d]).slice(0, 3)}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

WeeklyAvailability.propTypes = {
	// tous les créneaux définis : { id, day, hour, minute }
	timeslots: PropTypes.array,
	// sélection courante : { [timeslotId]: 'enable' | 'disable' }
	selection: PropTypes.object,
};

export default WeeklyAvailability;
